import { defineStore } from 'pinia';

import type { OccupationPrimary } from '@/types/models/occupation';
import type { ExperienceCompany } from '@/types/pages/ExperienceCompany';
import type { WorkHistoryPrimary } from '@/types/pages/WorkHistory';
import type { CandidateResumeIndustry } from '@/types/pages/candidate/OptionalInformation';

type CareerWorkHistory = NonNullable<WorkHistoryPrimary['candidate_resume_work_histories']>;

export interface CareerResumeOccupation {
  occupation_id: number;
  name?: string;
  parent_name: string;
  years_of_experience: number;
}

interface OptionalInformationPayload {
  candidate_resume_industries?: CandidateResumeIndustry[];
}

export interface CareerType {
  id?: number;
  income_id: string;
  experienced_companies_number: string;
  corporation_name: string;
  years_of_service: string;
  to_year: string;
  to_month: string;
  is_enrolled: boolean;
  from_month: string;
  from_year: string;
  employment_type_id: string;
  experienced_companies_remarks: string;
  candidate_resume_work_histories: CareerWorkHistory;
  candidate_resume_occupations: CareerResumeOccupation[];
  candidate_resume_experience: ExperienceCompany[];
  candidate_resume_industries: CandidateResumeIndustry[];
}

export const useCareerStore = defineStore('career.store', {
  state: (): CareerType => ({
    income_id: '',
    experienced_companies_number: '',
    corporation_name: '',
    years_of_service: '',
    to_year: '',
    to_month: '',
    is_enrolled: true,
    from_month: '',
    from_year: '',
    employment_type_id: '',
    experienced_companies_remarks: '',
    candidate_resume_work_histories: [],
    candidate_resume_occupations: [],
    candidate_resume_experience: [],
    candidate_resume_industries: [],
  }),

  actions: {
    setCareerData(data: CareerType) {
      this.id = data.id;
      this.income_id = data.income_id || '';
      this.experienced_companies_number = data.experienced_companies_number || '';
      this.corporation_name = data.corporation_name || '';
      this.years_of_service = data.years_of_service || '';
      this.to_year = data.to_year || '';
      this.to_month = data.to_month || '';
      this.is_enrolled = data.is_enrolled == null ? true : data.is_enrolled;
      this.from_month = data.from_month || '';
      this.from_year = data.from_year || '';
      this.employment_type_id = data.employment_type_id || '';
      this.experienced_companies_remarks = data.experienced_companies_remarks || '';
      this.candidate_resume_work_histories = data.candidate_resume_work_histories || [];
      this.candidate_resume_occupations = data.candidate_resume_occupations || [];
      this.candidate_resume_experience = data.candidate_resume_experience || [];
      this.candidate_resume_industries = data.candidate_resume_industries || [];
    },

    setOptionalInformationData(data: OptionalInformationPayload) {
      this.candidate_resume_industries = data.candidate_resume_industries || [];
    },

    createOccupations(occupations: OccupationPrimary[]) {
      this.candidate_resume_occupations = occupations
        .filter(
          (occupation): occupation is OccupationPrimary & { id: number } => occupation.id != null
        )
        .map(occupation => ({
          occupation_id: occupation.id,
          name: occupation.name,
          parent_name: occupation.parentName,
          years_of_experience: 0,
        }));
    },

    reset() {
      this.income_id = '';
      this.experienced_companies_number = '';
      this.corporation_name = '';
      this.years_of_service = '';
      this.to_year = '';
      this.to_month = '';
      this.is_enrolled = true;
      this.from_month = '';
      this.from_year = '';
      this.employment_type_id = '';
      this.experienced_companies_remarks = '';
      this.candidate_resume_work_histories = [];
      this.candidate_resume_occupations = [];
      this.candidate_resume_experience = [];
      this.candidate_resume_industries = [];
    },
  },
});
